// Revisado: 2025-12-18 [I18n Scan]
import { calculateAdjustedScore } from './SkillScoreCalculator';
import { calculateSkillCost } from './SkillCostCalculator';

/**
 * Calculates efficiency metrics for a single skill, considering evolution costs.
 * 
 * @param {Object} skillItem - The skill item object ({ skill, level, active, fastLearner, ... })
 * @param {Array} allActiveSkills - List of all active skill items (to look up base skills)
 * @param {Object} aptitudes - Character aptitudes
 * @returns {Object} Object with effectiveCost, effectiveScore, and efficiency
 */

export const calculateEffectiveMetrics = (skillItem, allActiveSkills, aptitudes) => {
    const { skill, level, fastLearner } = skillItem;

    // 1. Calculate Self Cost
    const baseCost = skill.cost || 0;
    const selfCost = calculateSkillCost(baseCost, level, fastLearner);

    // 2. Calculate Effective Cost (Base + Upgrade if applicable)
    let effectiveCost = selfCost;

    // Check if this is an Upgrade skill
    if (skill.base_skill_id) {
        // Find the base skill in the active list to get its CURRENT cost configuration
        const baseItem = allActiveSkills.find(item => parseInt(item.skill.Id_skill || item.skill.Id, 10) === parseInt(skill.base_skill_id, 10));

        // Only add base cost if the base skill is NOT active (i.e., we need to buy it)
        if (baseItem && !baseItem.active) {
            const baseBaseCost = baseItem.skill.cost || 0;
            const baseRealCost = calculateSkillCost(baseBaseCost, baseItem.level, baseItem.fastLearner);
            effectiveCost += baseRealCost;
        }
    }

    // 3. Calculate Effective Score
    // For upgrades, we use ONLY the upgrade's score (as requested), 
    // but the cost includes the base. This penalizes upgrades naturally unless they give a huge jump.
    const scoreResult = calculateAdjustedScore(skill, aptitudes);
    const effectiveScore = scoreResult.adjustedScore;

    // 4. Calculate Efficiency
    const efficiency = effectiveCost > 0 ? effectiveScore / effectiveCost : 0;

    return {
        ...skillItem,
        effectiveCost,
        effectiveScore,
        efficiency
    };
};

/**
 * Ranks all skills by their efficiency (Score / EffectiveCost).
 * Returns a Map of Skill ID -> Rank (1-based).
 * 
 * @param {Array} activeSkills - List of skill items from App state
 * @param {Object} aptitudes - Character aptitudes
 * @returns {Map} Map<SkillId, RankNumber>
 */
export const calculateEfficiencyRankings = (activeSkills, aptitudes) => {
    // 1. Filter for inactive skills only (User request: "skill activada debe eliminarse del calculo")
    const availableSkills = activeSkills.filter(item => !item.active);

    // 2. Calculate metrics for available skills
    const skillsWithMetrics = availableSkills.map(item => calculateEffectiveMetrics(item, activeSkills, aptitudes));

    // 3. Sort by Efficiency (Descending), then Score (Descending)
    skillsWithMetrics.sort((a, b) => {
        if (b.efficiency !== a.efficiency) {
            return b.efficiency - a.efficiency;
        }
        return b.effectiveScore - a.effectiveScore;
    });

    // 4. Create Rank Map
    const rankMap = new Map();
    skillsWithMetrics.forEach((item, index) => {
        rankMap.set(parseInt(item.skill.Id_skill || item.skill.Id, 10), index + 1);
    });

    return rankMap;
};

/**
 * Finds the optimal set of skills to maximize score within a budget using DP Knapsack.
 * Handles dependencies: Buying Upgrade implies paying for Base (if not already active).
 *
 * @param {Array} activeSkills - List of all active skills (with current levels/costs)
 * @param {Object} aptitudes - Current aptitudes
 * @param {number} budget - The point budget
 * @returns {Set} Set of Skill IDs to buy
 */
export const findOptimalSkillSet = (activeSkills, aptitudes, budget) => {
    // 1. Filter for available skills (inactive ones)
    const availableSkills = activeSkills.filter(item => !item.active);

    if (availableSkills.length === 0 || budget <= 0) return new Set();

    // 2. Group skills by Family (Base -> Upgrade)
    const skillFamilies = new Map();
    const processedIds = new Set();

    const getId = (item) => parseInt(item.skill.Id_skill || item.skill.Id, 10);

    // First pass: Identify Upgrades and their Bases
    availableSkills.forEach(item => {
        if (processedIds.has(getId(item))) return;

        if (item.skill.base_skill_id) {
            const baseId = parseInt(item.skill.base_skill_id, 10);
            const baseItem = availableSkills.find(s => getId(s) === baseId);

            if (baseItem) {
                skillFamilies.set(baseId, { base: baseItem, upgrade: item });
                processedIds.add(getId(baseItem));
                processedIds.add(getId(item));
                return;
            }
        }
    });

    // Second pass: Add remaining independent skills
    availableSkills.forEach(item => {
        if (processedIds.has(getId(item))) return;
        skillFamilies.set(getId(item), { base: item, upgrade: null });
    });

    // 3. Prepare Items for Knapsack
    const groups = [];

    skillFamilies.forEach((family) => {
        const groupOptions = [];

        // Option 1: Buy Base
        const baseMetrics = calculateEffectiveMetrics(family.base, activeSkills, aptitudes);
        groupOptions.push({
            ids: [parseInt(family.base.skill.Id_skill || family.base.skill.Id, 10)],
            cost: baseMetrics.effectiveCost,
            score: baseMetrics.effectiveScore
        });

        // Option 2: Buy Upgrade (if exists)
        if (family.upgrade) {
            const upgradeMetrics = calculateEffectiveMetrics(family.upgrade, activeSkills, aptitudes);
            groupOptions.push({
                ids: [parseInt(family.base.skill.Id_skill || family.base.skill.Id, 10), parseInt(family.upgrade.skill.Id_skill || family.upgrade.skill.Id, 10)],
                cost: upgradeMetrics.effectiveCost,
                score: upgradeMetrics.effectiveScore
            });
        }

        groups.push(groupOptions);
    });

    // 4. DP Knapsack
    // dp[w] = max score with budget w
    const dp = new Array(budget + 1).fill(0);
    // keep[groupIndex][w] = optionIndex selected for this group at this budget
    const keep = Array.from({ length: groups.length }, () => new Array(budget + 1).fill(-1));

    for (let i = 0; i < groups.length; i++) {
        const options = groups[i];

        // Iterate budget downwards to avoid using same item twice (standard 0/1 knapsack pattern)
        // But here we have mutually exclusive options for the same group.
        // We need to compare "Skip Group" vs "Option 1" vs "Option 2".
        // Since we iterate groups one by one, we can use a 1D DP array if we iterate budget downwards.

        for (let w = budget; w >= 0; w--) {
            let bestScore = dp[w]; // Default: Skip this group (keep previous max)
            let bestOption = -1;   // -1 means Skip

            for (let optIdx = 0; optIdx < options.length; optIdx++) {
                const { cost, score } = options[optIdx];
                if (w >= cost) {
                    // Check score from previous group (which is currently in dp[w-cost] before update?)
                    // Wait, if we iterate downwards, dp[w-cost] is from the PREVIOUS group iteration.
                    // So this is correct.
                    const prevScore = dp[w - cost];
                    const newScore = prevScore + score;

                    if (newScore > bestScore) {
                        bestScore = newScore;
                        bestOption = optIdx;
                    }
                }
            }

            dp[w] = bestScore;
            keep[i][w] = bestOption;
        }
    }

    // 5. Reconstruct Solution
    const optimalIds = new Set();
    let remainingBudget = budget;

    for (let i = groups.length - 1; i >= 0; i--) {
        const optionIdx = keep[i][remainingBudget];
        if (optionIdx !== -1) {
            const option = groups[i][optionIdx];
            option.ids.forEach(id => optimalIds.add(id));
            remainingBudget -= option.cost;
        }
    }

    return optimalIds;
};
